import { Col, Form, Row } from 'antd';
import { AInput, AModal, ASelect } from '../../components';
import { IPaymentMethod, IPaymentItemInsert } from '../../shared/interfaces';
import { useCallback, useState, useEffect } from 'react';
import { useToast } from '../../hooks/toast';
import { v4 as uuidv4 } from 'uuid';
import { formatToBRL, parseCurrencyInput } from '../../shared/utils/functions';
import { useI18n } from '../../shared/i18n';

interface IInsertPayment {
  open: boolean;
  close: () => void;
  setPaymentsItemsInsert: React.Dispatch<
    React.SetStateAction<IPaymentItemInsert[]>
  >;
  paymentItemInsert: IPaymentItemInsert | null;
  paymentsMethods: IPaymentMethod[];
  remaining: number;
}

const MAX_INSTALLMENTS = 24;

const InsertPayment: React.FC<IInsertPayment> = ({
  open,
  close,
  setPaymentsItemsInsert,
  paymentItemInsert,
  paymentsMethods,
  remaining,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const [id, setId] = useState<string>();
  const [payment, setPayment] = useState<string>();
  const [quantityInstallments, setQuantityInstallments] = useState<number>(1);
  const [value, setValue] = useState<number>(remaining);
  const [dueDate, setDueDate] = useState<string>();

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseCurrencyInput(e.target.value);
    setValue(numericValue);
  };

  const handleInsert = useCallback(() => {
    if (payment && quantityInstallments && value) {
      if (!paymentItemInsert) {
        const insert: IPaymentItemInsert = {
          id: uuidv4(),
          payment_id: payment,
          installment: quantityInstallments,
          value,
          due_date: dueDate,
        };
        setPaymentsItemsInsert((state) => [...state, insert]);
      } else {
        const edit: IPaymentItemInsert = {
          id,
          payment_id: payment,
          installment: quantityInstallments,
          value,
          due_date: dueDate,
        };
        setPaymentsItemsInsert((state) =>
          state.map((e) => (e.id !== edit.id ? e : edit)),
        );
      }
      close();
    } else {
      addToast({
        type: 'error',
        title: t('insertPayment.toast.errorTitle'),
        description: t('insertPayment.toast.fillAllFields'),
      });
    }
  }, [
    id,
    payment,
    quantityInstallments,
    value,
    dueDate,
    setPaymentsItemsInsert,
    paymentItemInsert,
    remaining,
    t,
  ]);

  useEffect(() => {
    if (paymentItemInsert) {
      setId(paymentItemInsert.id);
      setPayment(paymentItemInsert.payment_id);
      setQuantityInstallments(paymentItemInsert.installment);
      setValue(paymentItemInsert.value);
      setDueDate(paymentItemInsert.due_date);
    }
  }, [open]);

  const installmentOptions = Array.from(
    { length: MAX_INSTALLMENTS },
    (_, index) => {
      const value = index + 1;
      const label = t(
        value === 1
          ? 'insertPayment.installments.single'
          : 'insertPayment.installments.multiple',
        { count: value },
      );
      return { label, value };
    },
  );

  return (
    <AModal
      open={open}
      close={close}
      title={t('insertPayment.modal.title')}
      okText={t('insertPayment.modal.ok')}
      width={760}
      onOk={() => handleInsert()}
    >
      <Form layout="vertical" style={{ width: '100%' }}>
        <Row>
          <Col span={12}>
            <Form.Item label={t('insertPayment.fields.paymentMethod')}>
              <ASelect
                value={payment}
                placeholder={t('insertPayment.fields.paymentMethodPlaceholder')}
                onChange={(e) => setPayment(e)}
                allowClear
                options={paymentsMethods.map((fin) => ({
                  label: fin.description,
                  value: fin.id,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('insertPayment.fields.value')} required>
              <AInput
                min={0}
                value={formatToBRL(value)}
                onChange={handlePriceChange}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item label={t('insertPayment.fields.installments')}>
              <ASelect
                value={quantityInstallments}
                placeholder={t('insertPayment.fields.installmentsPlaceholder')}
                onChange={(e) => setQuantityInstallments(e)}
                options={installmentOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('insertPayment.fields.dueDate')}>
              <AInput
                value={dueDate}
                type="date"
                onChange={(e) => setDueDate(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </AModal>
  );
};

export default InsertPayment;
